// Properly extend the Window interface
import { getLogger } from './log';

const logger = getLogger('[Media]');

interface Window {
  localStream?: MediaStream | null;
  rtcPeerConnection?: RTCPeerConnection | null;
  mediaRecorder?: MediaRecorder | null;
}

const activeStreams: MediaStream[] = [];

// Patch getUserMedia to track all streams
export function patchGetUserMedia() {
  if (!navigator?.mediaDevices?.getUserMedia) return;
  const original = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
  navigator.mediaDevices.getUserMedia = async (constraints) => {
    const stream = await original(constraints);
    logger.debug(`[patchGetUserMedia] Patched stream`, stream);
    activeStreams.push(stream);
    return stream;
  };
}

export function closeAllMediaConnections(): void {
  if (!navigator.mediaDevices) return;

  // Stop all tracked streams
  activeStreams.forEach((stream) => {
    stream.getTracks().forEach((track) => {
      track.stop();
      logger.debug(`[closeAllMediaConnections] Stopped track`, track.kind);
    });
  });

  activeStreams.length = 0; // Clear the array

  const w = window as unknown as Window;

  // Stop local stream
  if (w.localStream) {
    w.localStream.getTracks().forEach((track) => {
      track.stop();
      logger.debug(`[closeAllMediaConnections] Stopped local track`, track.kind);
    });
    w.localStream = null;
  }

  // Close RTCPeerConnection
  if (w.rtcPeerConnection) {
    w.rtcPeerConnection.close();
    w.rtcPeerConnection = null;
    logger.debug('[closeAllMediaConnections] Closed RTCPeerConnection');
  }

  // Stop MediaRecorder
  if (w.mediaRecorder?.state !== 'inactive') {
    w.mediaRecorder?.stop();
    w.mediaRecorder = null;
    logger.debug('[closeAllMediaConnections] Stopped MediaRecorder');
  }

  // Force stop any remaining tracks
  navigator.mediaDevices
    .enumerateDevices()
    .then(() => logger.debug('[closeAllMediaConnections] All media connections closed successfully'));
}

// Call this early in your app initialization
patchGetUserMedia();
